import { FC, useState } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@/components';
import { isAzureAdEnabled } from '@/config/authConfig';
import MicrosoftLoginButton from '@/components/MicrosoftLoginButton';

const LoginPage: FC = () => {
  const { isAuthenticated, isLoading, login } = useAuthContext();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await login({ email, password });
    } catch (err: unknown) {
      // Backend usa custom_exception_handler que embrulha em `{error: {message, ...}}`.
      // Alguns DRF views legados ainda retornam `{detail}`. Cobrimos ambos.
      const axiosErr = err as {
        response?: { data?: { detail?: string; error?: { message?: string }; message?: string } };
      };
      const data = axiosErr?.response?.data;
      const msg = data?.error?.message || data?.detail || data?.message;
      setError(msg || 'Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMicrosoftSuccess = (result: { created: boolean; needsProfileCompletion: boolean }) => {
    if (result.created || result.needsProfileCompletion) {
      navigate('/completar-perfil');
    } else {
      navigate('/');
    }
  };

  const handleMicrosoftError = (errorMsg: string) => {
    setError(errorMsg);
  };

  return (
    <div className="tema-escuro-fixo flex min-h-screen items-center justify-center bg-[#001420] px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center">
          {/* Logo h.company — dominant element */}
          <img src="/hcompany-logo.png" alt="h.company" className="w-[280px] mb-6" />

          {/* h.lab name — styled with brand colors */}
          <h2 className="text-center text-4xl font-bold tracking-tight">
            <span className="text-[#7ED6DD]">h</span>
            <span className="text-[#FCB428]">.lab</span>
          </h2>

          {/* Subtitle */}
          <p className="mt-2 text-center text-sm text-[#7ED6DD]">
            Faça login para continuar
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {error && (
            <div className="rounded-lg bg-rose-500/10 border border-rose-500/20 px-4 py-3 text-sm text-rose-400">
              {error}
            </div>
          )}

          {isAzureAdEnabled ? (
            <>
              {/* SSO corporativo como ação principal */}
              <div className="space-y-4">
                <p className="text-center text-sm text-white/60">
                  Use sua conta corporativa para acessar a plataforma
                </p>
                <MicrosoftLoginButton
                  onSuccess={handleMicrosoftSuccess}
                  onError={handleMicrosoftError}
                />
              </div>

              {/* Link discreto para login com email/senha */}
              {!showEmailLogin ? (
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEmailLogin(true)}
                    className="w-full text-center text-xs text-white/30 hover:text-white/50 transition-colors"
                  >
                    Entrar com email e senha
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative pt-2">
                    <div className="absolute inset-0 flex items-center pt-2">
                      <div className="w-full border-t border-white/10" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-[#001420] px-2 text-white/30 text-xs">email e senha</span>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-3">
                      <input
                        id="email"
                        name="email"
                        type="text"
                        autoComplete="username"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="relative block w-full appearance-none rounded-lg border border-[rgba(252,180,40,0.15)] bg-[#003046] px-4 py-2.5 text-sm text-white placeholder-white/40 focus:z-10 focus:border-[#FCB428] focus:outline-none focus:ring-1 focus:ring-[#FCB428]"
                        placeholder="Email ou matrícula"
                      />
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="relative block w-full appearance-none rounded-lg border border-[rgba(252,180,40,0.15)] bg-[#003046] px-4 py-2.5 text-sm text-white placeholder-white/40 focus:z-10 focus:border-[#FCB428] focus:outline-none focus:ring-1 focus:ring-[#FCB428]"
                        placeholder="Senha"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="group relative flex w-full justify-center rounded-lg border border-[#FCB428]/30 bg-transparent px-3 py-2.5 text-sm font-medium text-[#FCB428] hover:bg-[#FCB428]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FCB428] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Entrando...' : 'Entrar'}
                    </button>
                  </form>
                </>
              )}

              {/* Link para cadastro por matrícula (US01 — Frente 1) */}
              <div className="pt-2 text-center">
                <Link
                  to="/cadastro-matricula"
                  data-testid="link-cadastro-matricula"
                  className="text-xs text-white/50 hover:text-[#FCB428] transition-colors"
                >
                  Não tenho email corporativo →
                </Link>
              </div>
            </>
          ) : (
            /* Fallback: login por email/matrícula + senha quando SSO está desativado */
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <input
                  id="email"
                  name="email"
                  type="text"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="relative block w-full appearance-none rounded-lg border border-[rgba(252,180,40,0.15)] bg-[#003046] px-4 py-3 text-white placeholder-white/40 focus:z-10 focus:border-[#FCB428] focus:outline-none focus:ring-1 focus:ring-[#FCB428] sm:text-sm"
                  placeholder="Email ou matrícula"
                />
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="relative block w-full appearance-none rounded-lg border border-[rgba(252,180,40,0.15)] bg-[#003046] px-4 py-3 text-white placeholder-white/40 focus:z-10 focus:border-[#FCB428] focus:outline-none focus:ring-1 focus:ring-[#FCB428] sm:text-sm"
                  placeholder="Senha"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative flex w-full justify-center rounded-lg bg-[#FCB428] px-3 py-3 text-sm font-semibold text-[#003046] hover:bg-[#FFC94D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FCB428] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Entrando...' : 'Entrar'}
              </button>

              {/* Link para cadastro por matrícula (US01 — Frente 1) */}
              <div className="text-center">
                <Link
                  to="/cadastro-matricula"
                  data-testid="link-cadastro-matricula"
                  className="text-xs text-white/60 hover:text-[#FCB428] transition-colors"
                >
                  Não tenho email corporativo →
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
